import { useLayoutEffect, useRef, useState } from "react";
import { resolveFontFamily } from "./fonts";
import { logoHeightPx } from "./logoSizes";
import {
  watermarkOpacity,
  watermarkSizePercent,
  watermarkVerticalPositionPercent,
} from "./watermarkDefaults";
import { SLIDESHOW_BANNER_MODES, WATERMARK_MODES, resolveWatermarkMode } from "score-common/constants";
import VenueClock from "./VenueClock";
import BlankLayout from "./BlankLayout";
import ScoreboardErrorBoundary from "./ScoreboardErrorBoundary";
import { DEFAULT_BACKGROUND_COLOR, DEFAULT_CLOCK_BAR_COLOR } from "./scoreboardDefaults";
import { resolveCanvasSize } from "./aspectRatio";
import { useScreenTransition, TRANSITION_PHASE } from "./useScreenTransition";
// #1790 — this AC originally read brand/favicon/favicon.svg (the "only
// SQUARE Fox Village artwork" reasoning). That reasoning was already
// rejected once, for the exact same asset at the exact same blown-up scale,
// in blankLogoAssets.js's header (Jaybill, 2026-08-20): the favicon is
// "vestigial and NOT correct" and reads as a box/crate, not a brand mark,
// and "square" was never a real constraint. Using fox-village-mark-white.svg
// instead applies that same recorded decision here rather than re-making it.
import fvdMark from "brand/logo/fox-village-mark-white.svg";
import "./ScoreboardFrame.css";

// #1637 — the public board's shell (design §4.1, §8.4). Owns: theme
// application as CSS custom properties, logo slots, and the named EMPTY
// slots for SponsorStrip (#1639) and QRCorner (#1641) — this card does not
// implement either. The 3 layout families (Summary / Single entry / Ranked
// list) that fill `children` are #1652.
//
// #1692 — also owns the always-present venue clock, originally the
// bottom-right corner alongside the QR slot (#1641). #1790 moved the clock
// into the new full-width footer banner (left side) and dropped the
// staleness indicator this file used to also own outright, with no
// replacement liveness signal — the clock is now the only one. Placed HERE
// rather than in any one mode type/layout family so it is guaranteed on
// every one of them without touching #1652's per-family components, same
// reasoning as before the move. #1946 later moved the QR slot (#1641) into
// this same banner, on its right side alongside the brand mark — see the
// footer banner block below for why.

// #1798 — re-exported (see scoreboardDefaults.js's header for why the value
// itself lives in its own module, not here) so ScoreboardErrorBoundary's own
// self-contained fallback (which cannot rely on this component's CSS custom
// properties) uses the exact same color rather than a second hardcoded
// literal that could drift from this one.
export { DEFAULT_BACKGROUND_COLOR };
const DEFAULT_TEXT_COLOR = "#f5f5f7";
const DEFAULT_ACCENT_COLOR = "#f5a623";

// #2498 AC1 — the fixed design canvas. Every vw/vh value the layout-family
// CSS used to compute against the real viewport was measured "in real
// Chrome at 1920x1080" (RankedListLayout.css's own comments, cited on the
// card), so this canvas is that same 1920x1080, not a re-derived constant.
const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;

// #2640 / #2499 -- the canvas no longer has one fixed shape: aspectRatio.js
// owns the three-entry table (2:1 1920x960, 16:9 1920x1080, 4:3 1440x1080)
// and `resolveCanvasSize`, which prefers a DECLARED ratio (the Screen's, via
// the shell's ?aspectRatio= param) over snapping the board's own box. The
// table is deliberately duplicated as a literal (not imported) in the
// Cypress specs (scoreboard-canvas-letterbox.cy.js,
// scoreboard-canvas-parity-screenshots.cy.js, scoreboard-declared-aspect.cy.js)
// so those specs prove the OBSERVABLE behavior, not the implementation
// checking itself.

// #2498 AC1 — `scale = min(ownerWidth / canvasWidth, ownerHeight /
// canvasHeight)`, measured off .scoreboard-frame's OWN rendered box
// (ResizeObserver), same idiom as AnnouncerFrame.jsx's #2453 own-box
// measurement, not `window.innerWidth`/`window.innerHeight` directly: the
// "fixed" variant's box IS the viewport (CSS `position: fixed; inset: 0`),
// but the "embedded" variant's box is the theme editor's small preview pane
// (#1640), so measuring the frame's own box is what makes one formula
// correct for both variants instead of special-casing embedded.
// `useLayoutEffect` + an immediate synchronous `getBoundingClientRect()` read
// (not just the ResizeObserver's own async first callback) so the first
// paint already uses the right scale instead of flashing scale:1 (or
// scale:0 pre-measurement) for one frame — same reasoning as AnnouncerFrame's
// #2453 comment. `typeof ResizeObserver === "undefined"` guard: jsdom (Jest)
// has no ResizeObserver; the scale then stays at its initial null and
// callers get `scale ?? 1` against the default (16:9) canvas size, which
// keeps existing non-visual Jest assertions (querying by data-testid, not by
// rendered pixel size) working unchanged.
// #2640 — also returns `canvasWidth`/`canvasHeight`, resolved from the same
// measurement via `resolveCanvasSize`, so the caller can size the canvas
// element itself, not just scale it.
function useCanvasScale(declaredAspectRatio, fillCanvas) {
  const ownerRef = useRef(null);
  // The measured owner box only. The canvas shape is derived from it AT RENDER
  // (resolveCanvasSize), so a DECLARED ratio (#2499) takes effect even before
  // any measurement exists (jsdom, a pre-layout paint), and a changed
  // declaration re-derives without a re-measure.
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    const el = ownerRef.current;
    if (!el) return undefined;
    const measure = (width, height) => {
      if (!width || !height) return;
      setBox({ width, height });
    };
    const rect = el.getBoundingClientRect();
    measure(rect.width, rect.height);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entriesList) => {
      const entry = entriesList[0];
      if (entry) measure(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const canvasSize = resolveCanvasSize(box?.width, box?.height, declaredAspectRatio, fillCanvas);
  return {
    ownerRef,
    scale: box ? Math.min(box.width / canvasSize.width, box.height / canvasSize.height) : null,
    canvasWidth: canvasSize.width,
    canvasHeight: canvasSize.height,
    letterbox: canvasSize.letterbox,
  };
}

function backgroundCssValue(background) {
  if (!background) return DEFAULT_BACKGROUND_COLOR;
  if (background.kind === "gradient" && background.colorBottom) {
    return `linear-gradient(to bottom, ${background.color}, ${background.colorBottom})`;
  }
  return background.color || DEFAULT_BACKGROUND_COLOR;
}

function themeCssVars(theme) {
  return {
    "--scoreboard-bg": backgroundCssValue(theme?.background),
    "--scoreboard-text-color": theme?.textColor || DEFAULT_TEXT_COLOR,
    "--scoreboard-accent-color": theme?.accentColor || DEFAULT_ACCENT_COLOR,
    "--scoreboard-font-family": resolveFontFamily(theme?.fontKey),
    // Card #2301 — same fallback shape as the three vars above: a
    // theme predating this field, or one that explicitly carries
    // clockBarColor: null, both resolve to the footer banner's own
    // pre-existing #147793 fox-blue appearance (DEFAULT_CLOCK_BAR_COLOR)
    // rather than an unset CSS custom property. This is the color actually
    // visible on a real board (the footer banner below), not the retired
    // VenueClock.css pill.
    "--scoreboard-clock-bar-color": theme?.clockBarColor || DEFAULT_CLOCK_BAR_COLOR,
  };
}

// Card #2321 HOTFIX, live show: superseded server-side fix. Prod measurement
// on this card recorded X-Amz-Credential varying across 9 of 12 polls
// roughly 1s apart for one UNCHANGED S3 key -- the short-date component of
// that field cannot change in 12 seconds, so the AKID itself changed:
// distinct warm Lambda containers hold distinct session credentials with no
// shared signing state, so no amount of server-side timestamp rounding can
// make two containers emit a byte-identical string. Jaybill's ruling
// (2026-09-05, lifting this card description's own original rejection of
// this exact approach): stabilise here instead, at the one place every
// presigned asset URL this frame renders actually lands.
//
// The S3 KEY (the path segment before "?") is the part that identifies the
// underlying object and is what the card's own prod measurement found
// IDENTICAL across all 12 polls even while the signature churned -- so
// comparing on that, not on the full URL string, is what tells "same image,
// fresh signature" (keep rendering the old string, no DOM change, no
// repaint) apart from "a genuinely different asset" (swap).
function s3KeyOf(url) {
  if (!url) return url;
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

// Card #2327 -- the pin above had no time bound. A presigned URL carries
// X-Amz-Date (signing time, compact ISO-8601 UTC) and X-Amz-Expires (seconds
// valid), so its REAL expiry is readable from the URL itself. score-api
// (ASSET_URL_TTL_SECONDS=3600, PRESIGN_REFRESH_MARGIN_MS=5min,
// apps/score-api/src/lib/scoreboard-assets.js) hands out a URL only once it
// has MORE than 5 minutes of life left, so a freshly arrived URL always
// clears the 2 minute margin below with room to spare, and a pinned one is
// released with the old signature still valid for ~2 minutes (a browser that
// re-requests the old string in that window still succeeds). Releasing
// costs one repaint (one blink) per hold-window, which is the accepted
// tradeoff against a kiosk silently losing its watermark/logo to a 403.
// A URL with no parseable X-Amz-Date + X-Amz-Expires (a non-presigned or
// fixture URL) has no known expiry and is held exactly as before #2327.
export const ASSET_URL_RELEASE_MARGIN_MS = 2 * 60 * 1000;

// X-Amz-Date is "YYYYMMDDTHHMMSSZ" (SigV4 basic format). Returns epoch ms of
// the signature's expiry, or null when either field is absent/malformed.
export function signedUrlExpiryMs(url) {
  if (!url) return null;
  const queryIndex = url.indexOf("?");
  if (queryIndex === -1) return null;
  const params = new URLSearchParams(url.slice(queryIndex + 1));
  const date = params.get("X-Amz-Date");
  const expires = params.get("X-Amz-Expires");
  const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(date || "");
  if (!match || !/^\d+$/.test(expires || "")) return null;
  const [, y, mo, d, h, mi, sec] = match;
  const signedAtMs = Date.UTC(+y, +mo - 1, +d, +h, +mi, +sec);
  return signedAtMs + Number(expires) * 1000;
}

// One ref-backed Map per ScoreboardFrame instance (never module-scope state,
// so two boards -- or two renders of the theme editor's live-preview pane,
// #1640 -- never share entries), keyed by a caller-chosen stable id (the
// watermark, or a given logo slot). `stabilizeAssetUrl(id, url)` funnels
// EVERY presigned asset this frame renders through the same rule: return the
// incoming url the first time an id is seen or whenever its S3 key actually
// changes OR the pinned signature is within ASSET_URL_RELEASE_MARGIN_MS of
// its own expiry (#2327), otherwise return the PREVIOUS url for that id so
// an unchanged asset's re-signed string never reaches the DOM as something
// new.
export function useAssetUrlStabilizer() {
  const cacheRef = useRef(new Map());
  return function stabilizeAssetUrl(id, url) {
    const cache = cacheRef.current;
    const key = s3KeyOf(url);
    const hit = cache.get(id);
    if (
      hit &&
      hit.key === key &&
      (hit.expiresAtMs === null || Date.now() < hit.expiresAtMs - ASSET_URL_RELEASE_MARGIN_MS)
    ) {
      return hit.url;
    }
    cache.set(id, { key, url, expiresAtMs: signedUrlExpiryMs(url) });
    return url;
  };
}

export default function ScoreboardFrame({
  theme,
  isLoading = false,
  sponsorSlot = null,
  qrSlot = null,
  // Card #3614 -- the bell-to-entry countdown, rendered in the footer's right
  // group beside the QR. null (every board without a running timer, and the
  // theme-editor preview) renders nothing.
  timerSlot = null,
  // Card #2484 -- mode-aware caption (Mid-Class Result says "standings").
  qrCaption = "Scan for results",
  // "fixed" (default): the real public board, viewport-sized/chrome-free.
  // "embedded": card #1640's theme editor live-preview pane reuses this
  // same component with sample data (design §8.3) inside a small contained
  // box, not the actual browser viewport — this swaps position:fixed for
  // position:absolute (scoreboard-frame--embedded, ScoreboardFrame.css) so
  // it fills its own positioned ancestor instead of covering the screen.
  variant = "fixed",
  // #1798 AC2 — forwarded straight through to the content-level
  // ScoreboardErrorBoundary below. ScoreboardPage passes the same
  // `lastUpdated`-derived resetKey/logoAssetUrl (card #2308: the server-
  // resolved URL, not a hardcoded key) it gives its OWN outer boundary, so
  // both clear together on the next successful poll.
  resetKey = null,
  logoAssetUrl = null,
  onRenderStatus = null,
  // #2499 -- the Screen's declared aspect ratio ("2:1" | "16:9" | "4:3"), when
  // the board was reached through one. null/undefined (the legacy route, no
  // Screen) snaps from the real box; any other non-enum value is 16:9.
  aspectRatio = null,
  // Card #2500 -- true when the board is showing a layout family tuned for
  // the canvas shape itself (the ranked-list family, class-summary detailed,
  // the single-entry family and Mid-Ride Simple, Message, and every Blank
  // (#2502), boardFillsCanvas). Such a
  // board fills a declared 4:3 canvas instead of being letterboxed inside it.
  // Absent/false is the pre-2500 behaviour exactly.
  fillCanvas = false,
  // Card #2489 -- the CURRENT mode's own `watermarkMode` ("inherit" | "show"
  // | "hide", score-common WATERMARK_MODES). Absent/unknown reads as
  // inherit (resolveWatermarkMode), which is today's behaviour exactly, so
  // every caller that never passes it (the theme editor preview, the
  // loading state) renders as before. Only "hide" changes anything: "show"
  // is a documented no-op beyond inherit (it cannot render a watermark the
  // theme has no url for).
  watermarkMode = null,
  // Card #3308 -- what counts as "a different screen" (screenTransition.js
  // screenKeyFor). When it changes, the CONTENT area runs the theme's
  // transition (default: fade out, then back in); the footer bar never does.
  // undefined (the theme editor's embedded preview, any caller that does not
  // pass it) turns transitions off entirely.
  screenKey = undefined,
  // The slideshow's own slide-to-slide transition ({type, durationMs} or
  // null = inherit the theme's).
  slideshowTransition = null,
  // Card #3314 -- an Image Loop's banner setting, "none" | "below" | "overlay"
  // (score-common SLIDESHOW_BANNER_MODES). null/absent (every board that is not
  // showing an Image Loop slide) is today's footer banner, exactly as before.
  //   none:    the footer banner is not rendered.
  //   overlay: the banner is drawn over the bottom of the content, which then
  //            has the whole canvas (ScoreboardFrame.css).
  bannerMode = null,
  children,
}) {
  const logos = Array.isArray(theme?.logos) ? theme.logos : [];
  const stabilizeAssetUrl = useAssetUrlStabilizer();
  const stabilizedWatermarkUrl = stabilizeAssetUrl("watermark", theme?.watermark?.url);
  // #2489 -- "hide" suppresses the layer for this mode only (no DOM node, same
  // "no empty layer" posture as a theme with no watermark). The stabilizer
  // above runs unconditionally (it is a hook, and keeping it warm means a
  // hide -> inherit switch does not re-key the unchanged S3 asset).
  const watermarkUrl =
    resolveWatermarkMode(watermarkMode) === WATERMARK_MODES.HIDE ? null : stabilizedWatermarkUrl;
  const slideBannerMode =
    bannerMode === SLIDESHOW_BANNER_MODES.NONE || bannerMode === SLIDESHOW_BANNER_MODES.OVERLAY ? bannerMode : null;
  const frameClassName = [
    "scoreboard-frame",
    variant === "embedded" ? "scoreboard-frame--embedded" : null,
    slideBannerMode ? `scoreboard-frame--banner-${slideBannerMode}` : null,
  ]
    .filter(Boolean)
    .join(" ");
  const { ownerRef, scale, canvasWidth, canvasHeight, letterbox } = useCanvasScale(aspectRatio, fillCanvas);
  // #2498 AC3 — `?? 1` (not `?? 0`) as the pre-measurement fallback: scale:1
  // is exactly correct at 1920x1080, so an un-measured first paint at the
  // reference size renders identically either way, and it never has to
  // render invisible (scale:0) content while jsdom or a pre-layout paint has
  // no ResizeObserver reading yet.
  //
  // Finding 4 follow-up (Opus judge, 2026-09-16; re-measured directly by a
  // later agent, 2026-09-16) — at scale 1 the transform is applied as NO
  // transform at all (undefined), not `scale(1)`. This sandbox's
  // element-scoped screenshot of the footer banner at 1920x1080 shows the
  // banner background not filling the captured width (getBoundingClientRect
  // on the same element reports the correct, un-shifted 1920-wide box, so
  // the DOM geometry is right and only the captured pixels are wrong).
  // 🔴 CORRECTED: an earlier version of this comment claimed that mispaint
  // was "reproduced with `transform: scale(1)` present, gone once it is
  // omitted" -- that causal claim was never actually re-run and is FALSE.
  // Directly A/B tested (forcing `canvasStyle` to always apply
  // `transform: scale(1)` vs. this branch's omission, same fixture, same
  // element-scoped screenshot): the mispainted region was byte-identical in
  // both captures (banner color started at the same x offset in both PNGs).
  // Presence or absence of the transform neither causes nor fixes it, so
  // this branch is NOT a workaround for that artifact -- matching
  // 7b1d057f's own commit body ("omitting the transform ... did NOT change
  // the diff numbers either"), which this comment previously contradicted.
  // The artifact itself remains AC3's open, capture-environment-only issue
  // (see the card comment), not a code defect this branch addresses.
  // This branch is kept anyway on its own, independent merit: omitting an
  // identity transform at scale 1 means the reference size renders through
  // the exact same paint path (no composited layer) as the pre-#2498 code,
  // which is what makes AC3's "byte-identical at 1920x1080" true by
  // construction rather than by a compositor's good behavior.
  const effectiveScale = scale ?? 1;
  // #2640 AC1/AC4 — the canvas element's own width/height are only added to
  // its inline style when they differ from the CSS defaults (1920x1080,
  // ScoreboardFrame.css's `.scoreboard-frame__canvas` rule): at 16:9 (the
  // pre-2640 shape) `canvasWidth`/`canvasHeight` ARE those defaults, so this
  // stays `{}` exactly as before this card and the 16:9 output remains
  // pixel-identical (AC4) — no inline style diff at all, not just an
  // equal-valued one. At 2:1, `canvasWidth`/`canvasHeight` resolve to
  // 1920x960 (aspectRatio.js) and this overrides the CSS default so the
  // canvas itself (not just its scale) takes the snapped shape.
  const isDefaultCanvasSize = canvasWidth === CANVAS_WIDTH && canvasHeight === CANVAS_HEIGHT;
  const canvasStyle = {
    ...(effectiveScale === 1 ? {} : { transform: `scale(${effectiveScale})` }),
    ...(isDefaultCanvasSize ? {} : { width: `${canvasWidth}px`, height: `${canvasHeight}px` }),
  };

  // Card #3308 -- the content area's body, built once so the transition hook
  // can hold on to the OLD screen's node while it fades out.
  const contentNode = (
    <>
        {/* #1798 AC4 — before the first successful poll ever lands, and for
            as long as every poll keeps failing (a persistent network fault
            or a poll timeout, not just the first request), this used to be
            a "Loading board…" text state with no way out — an indefinite
            spinner if the first poll never succeeds at all. Blank+logo is
            the universal fallback here too now: correct at t=0 (nothing has
            loaded yet, which IS "nothing to show"), and correct forever if
            nothing ever loads, since it is a resting state, not a stuck one. */}
        {isLoading ? (
          <BlankLayout theme={theme} />
        ) : (
          // #1798 AC2 — a boundary around CONTENT ONLY, nested inside the
          // one ScoreboardPage.jsx already wraps around this whole frame.
          // A throw from `children` (ScoreboardContent, a layout family
          // component) is caught HERE first, so the frame chrome around
          // it — the logo slots above, the sponsor/QR slots and the footer
          // banner below — stays up exactly as #1692/#1788 intend: #1790's
          // removal of the staleness line left the clock (now in the
          // banner) as the audience's only liveness cue, and losing it on a
          // content-only fault would be an unasked-for behavior change this
          // card does not own. A
          // throw from sponsorSlot/qrSlot (built in ScoreboardPage, not
          // here) is NOT inside this boundary, so it still propagates to
          // ScoreboardPage's outer boundary and replaces the whole frame —
          // that is deliberate, not a gap: those two slots are siblings of
          // this content div, not descendants of it.
          <ScoreboardErrorBoundary
            variant="canvas"
            resetKey={resetKey}
            logoAssetUrl={logoAssetUrl}
            theme={theme}
            onRenderStatus={onRenderStatus}
          >
            {children}
          </ScoreboardErrorBoundary>
        )}
    </>
  );
  const transitionState = useScreenTransition({
    screenKey,
    themeTransition: theme?.transition,
    slideshowTransition,
    content: contentNode,
  });

  const canvasChildren = (
    <>
      {/* #1943 — the venue watermark layer (design doc's own CSS block on
          the card): rendered FIRST so it paints under every other frame
          child in source order, at z-index 0 while .scoreboard-frame__content
          and .scoreboard-frame__footer-banner sit at z-index 1 (CSS). Gated
          on theme.watermark.url specifically (not just theme.watermark being
          set) so a theme with no watermark configured renders NO DOM node at
          all — AC3's "no empty layer" literally, not just an invisible one. */}
      {watermarkUrl ? (
        <div
          className="scoreboard-frame__watermark"
          data-testid="scoreboard-watermark"
          style={{
            // Quoted: an unquoted CSS url() cannot safely carry parens,
            // quotes, or whitespace, and a brand-asset presigned URL (query
            // string of arbitrary characters) is not guaranteed free of
            // them. #2321 hotfix: `watermarkUrl` is the STABILIZED value
            // (see useAssetUrlStabilizer above), not theme.watermark.url
            // directly -- a poll that only re-signs an unchanged S3 key
            // must never reach this backgroundImage as a new string.
            backgroundImage: `url("${watermarkUrl}")`,
            backgroundSize: `auto ${watermarkSizePercent(theme.watermark.size)}%`,
            backgroundPosition: `center ${watermarkVerticalPositionPercent(theme.watermark.verticalPosition)}%`,
            opacity: watermarkOpacity(theme.watermark.opacity),
          }}
        />
      ) : null}

      {logos.map((logo, index) => (
        <img
          key={`${logo.slot}-${index}`}
          // #2321 hotfix: same stabilizer as the watermark above, funneling
          // theme logos through the identical S3-key-comparison rule (the
          // card's own "fixes the watermark AND theme logos" intent, now
          // applied FE-side) -- id is per-slot-per-position so reordering
          // never cross-contaminates two logos' cached entries.
          src={stabilizeAssetUrl(`logo:${logo.slot}-${index}`, logo.url)}
          alt=""
          className={`scoreboard-frame__logo scoreboard-frame__logo--${logo.slot}`}
          // #2498 AC2 — px, not vh: this style renders INSIDE the fixed
          // 1920x1080 canvas, so a vh value here would resolve against the
          // real viewport and get scaled a second time by the canvas
          // transform. logoHeightPx already returns the design-size px
          // equivalent (logoSizes.js).
          style={{ height: `${logoHeightPx(logo.size)}px` }}
          data-testid={`scoreboard-logo-${logo.slot}`}
        />
      ))}

      {/* Card #3308 -- the content layers, ALWAYS a keyed list so React keeps
          each layer's element across a transition. At rest there is exactly
          one (the same DOM as before transitions existed). A crossfade keeps
          the old layer under its old key, now marked --outgoing (frozen,
          fading out, stopping above the footer banner), and mounts the new
          screen as the next key: the old screen is the SAME element that was
          live (no remount, no re-fired mount effects), and two crossfades in a
          row each start their own animation because each live layer is a
          fresh element. Both are siblings of the footer banner, never
          ancestors of it. */}
      {[
        transitionState.outgoing ? (
          <div
            key={transitionState.layer - 1}
            className="scoreboard-frame__content scoreboard-frame__content--outgoing"
            data-testid="scoreboard-content-outgoing"
            aria-hidden="true"
            style={{ "--scoreboard-transition-ms": `${transitionState.durationMs}ms` }}
          >
            {transitionState.outgoing}
          </div>
        ) : null,
        <div
          key={transitionState.layer}
          className={`scoreboard-frame__content${
            transitionState.phase === TRANSITION_PHASE.IDLE
              ? ""
              : ` scoreboard-frame__content--${transitionState.phase}`
          }`}
          data-transition-phase={
            transitionState.phase === TRANSITION_PHASE.IDLE ? undefined : transitionState.phase
          }
          style={
            transitionState.phase === TRANSITION_PHASE.IDLE
              ? undefined
              : { "--scoreboard-transition-ms": `${transitionState.durationMs}ms` }
          }
        >
          {transitionState.content}
        </div>,
      ]}

      {/* Named slot for the sponsor strip — card #1639 fills this in;
          deliberately empty here. */}
      <div className="scoreboard-frame__sponsor-slot" data-testid="scoreboard-sponsor-slot">
        {sponsorSlot}
      </div>

      {/* #1790 — full-width footer banner: clock (left) + QR/brand-mark
          group (right), white text/logo. Rendered as a sibling of
          .scoreboard-frame__content, OUTSIDE the isLoading ternary above,
          so it is frame-level and unconditional across every mode type
          including Blank and Message (AC11) — same placement pattern the
          venue clock/QR slot already used before this move. A normal
          (non-absolutely-positioned) flex child of .scoreboard-frame,
          so .scoreboard-frame__content's flex:1 yields it real vertical
          space rather than the banner overlaying content underneath.
          NOT the same element as Banner.jsx/Banner.css (card #1652's
          unrelated ranked-list banner, data-testid="banner") — distinct
          class/testid to avoid colliding with that existing component.
          Card #2301 — background used to be a hardcoded inline
          `colors.foxBlue`. This is the actual "clock bar" a venue sees
          (see themeCssVars above), so it now reads the theme's
          --scoreboard-clock-bar-color custom property, with an inline
          var() fallback to the exact same #147793 in case this element
          ever renders somewhere the property isn't set on an ancestor
          (belt-and-braces on top of themeCssVars always setting it). Text
          color stays hardcoded white regardless of theme (ScoreboardFrame.css
          `.scoreboard-frame__footer-banner`), so only background is themed
          here. */}
      {slideBannerMode === SLIDESHOW_BANNER_MODES.NONE ? null : (
      <div
        className="scoreboard-frame__footer-banner"
        data-testid="scoreboard-footer-banner"
        style={{ backgroundColor: "var(--scoreboard-clock-bar-color, #147793)" }}
      >
        <VenueClock />
        {/* #1946 — the QR (#1641) used to float above this banner at
            bottom-right (.scoreboard-frame__bottom-right, since removed),
            which collided with the last ranked-list row once a division
            owned the full board width. It now lives inside the banner,
            next to the brand mark, so the content area (flex:1 above this
            banner) is clear top to bottom on every mode type — the same
            guarantee that already protected the clock. QR content, module
            size and quiet zone are untouched (QRCorner.jsx/.css, still
            9.5vh (102.6px)); only its parent changed. `.scoreboard-frame__qr-slot:empty`
            (CSS) collapses this slot to zero width when no QR is configured,
            so an unconfigured board leaves no gap before the brand mark. */}
        <div className="scoreboard-frame__footer-right">
          {timerSlot ? (
            <div className="scoreboard-frame__timer-slot" data-testid="scoreboard-timer-slot">
              {timerSlot}
            </div>
          ) : null}
          {/* #2270 AC7 (Jaybill, 2026-09-04, matching demo pages p4/p5) — a
              bare code tells an audience nothing about what scanning it
              gets them, and the QR is the only thing on the board that
              leads anywhere. Rendered as a sibling of the qr-slot (not
              inside it) so it tracks the QR itself: gated on `qrSlot`
              being present, the same condition the slot's own `:empty`
              CSS rule uses, so an unconfigured board's caption disappears
              along with the code rather than captioning empty space. */}
          {qrSlot ? (
            <span className="scoreboard-frame__qr-label" data-testid="scoreboard-qr-label">
              {qrCaption}
            </span>
          ) : null}
          <div className="scoreboard-frame__qr-slot" data-testid="scoreboard-qr-slot">
            {qrSlot}
          </div>
          <img src={fvdMark} alt="" className="scoreboard-frame__footer-banner-logo" />
        </div>
      </div>
      )}
    </>
  );

  return (
    <div
      ref={ownerRef}
      className={frameClassName}
      style={themeCssVars(theme)}
      data-testid="scoreboard-frame"
    >
      <div className="scoreboard-frame__canvas" style={canvasStyle} data-testid="scoreboard-canvas">
        {letterbox ? (
          // #2499 -- a declared 4:3 canvas (1440x1080): the layout families are
          // tuned for 1920x1080 and cannot reflow, so the existing 16:9 layout
          // renders at its design size inside this inner box, scaled to fit the
          // canvas and centred; the theme background fills the bars. Nothing
          // clips. Card 2500 / #3110 / #2501: the ranked-list family, class-summary
          // detailed, the single-entry family, Mid-Ride Simple, Message and Blank
          // (#2502) are tuned for 4:3 and skip this box (`fillCanvas`), so
          // they never reach here.
          <div
            className="scoreboard-frame__letterbox"
            data-testid="scoreboard-letterbox"
            style={{
              width: `${letterbox.width}px`,
              height: `${letterbox.height}px`,
              left: `${letterbox.left}px`,
              top: `${letterbox.top}px`,
              transform: `scale(${letterbox.scale})`,
            }}
          >
            {canvasChildren}
          </div>
        ) : (
          canvasChildren
        )}
      </div>
    </div>
  );
}
